import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  useTheme,
  Fade,
  Slide,
  Avatar,
  Stack,
} from "@mui/material";
import {
  Science,
  Inventory,
  BookOnline,
  Analytics,
  Security,
  ArrowForward,
  Dashboard,
  NotificationsActive,
  AutoAwesome,
  Rocket,
  TrendingUp,
  Psychology,
  Engineering,
  Biotech,
  Build,
  Handyman,
  AssignmentTurnedIn,
} from "@mui/icons-material";
import { styled, keyframes } from "@mui/material/styles";

// Advanced Animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-15px) rotate(2deg);
  }
  66% {
    transform: translateY(-5px) rotate(-1deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: `
    radial-gradient(circle at 20% 50%, ${theme.palette.primary.main}15 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, ${theme.palette.secondary.main}10 0%, transparent 50%),
    radial-gradient(circle at 40% 80%, ${theme.palette.primary.main}08 0%, transparent 50%),
    linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)
  `,
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23${theme.palette.primary.main.replace(
      "#",
      ""
    )}' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='3'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.5,
  },
}));

const GlassmorphismCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${theme.palette.background.paper}95 0%, 
    ${theme.palette.background.paper}80 100%)`,
  backdropFilter: "blur(20px)",
  border: `1px solid ${theme.palette.divider}30`,
  borderRadius: "24px",
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}15, transparent)`,
    transition: "left 0.5s",
  },
  "&:hover": {
    transform: "translateY(-12px) scale(1.02)",
    boxShadow: `0 20px 40px ${theme.palette.primary.main}20`,
    "&::before": {
      left: "100%",
    },
  },
}));

const FloatingIcon = styled(Box)(({ delay = 0 }) => ({
  animation: `${float} 6s ease-in-out ${delay}s infinite`,
  filter: "drop-shadow(0 8px 32px rgba(0,0,0,0.1))",
}));

const GradientButton = styled(Button)(
  ({ theme, variant: buttonVariant = "primary" }) => {
    const gradients = {
      primary: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
      success: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
      warning: `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
    };

    return {
      background: gradients[buttonVariant],
      border: 0,
      borderRadius: "50px",
      color: "white",
      padding: "16px 32px",
      fontSize: "1.1rem",
      fontWeight: "bold",
      textTransform: "none",
      animation: `${pulse} 3s ease-in-out infinite`,
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: "-100%",
        width: "100%",
        height: "100%",
        background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)`,
        animation: `${shimmer} 2s infinite`,
      },
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: `0 12px 24px ${theme.palette.primary.main}40`,
        "&::before": {
          animation: `${shimmer} 0.8s infinite`,
        },
      },
    };
  }
);

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: "center",
  background: `linear-gradient(135deg, 
    ${theme.palette.background.paper} 0%, 
    ${theme.palette.primary.main}05 100%)`,
  borderRadius: "20px",
  border: `1px solid ${theme.palette.primary.main}15`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&::after": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: `conic-gradient(from 0deg, transparent, ${theme.palette.primary.main}10, transparent)`,
    animation: `${rotate} 8s linear infinite`,
    opacity: 0,
    transition: "opacity 0.3s",
  },
  "&:hover": {
    transform: "translateY(-8px) scale(1.02)",
    boxShadow: theme.shadows[20],
    "&::after": {
      opacity: 1,
    },
  },
}));

const FeatureIcon = styled(Avatar)(({ theme, color }) => ({
  width: 80,
  height: 80,
  background: `linear-gradient(135deg, ${color}20, ${color}40)`,
  margin: "0 auto 24px",
  border: `3px solid ${color}30`,
  "& svg": {
    fontSize: "2.5rem",
    color: color,
  },
}));

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Laboratory feature grouping
  const labFeatures = [
    {
      icon: <Inventory />,
      title: "Inventory Lifecycle",
      description:
        "Multi-category stock (consumables, instruments, essentials) with location depth & thresholds.",
      color: theme.palette.primary.main,
    },
    {
      icon: <BookOnline />,
      title: "Slot & Asset Booking",
      description:
        "Conflict-aware reservations for labs & equipment; faculty direct booking & scholar requests.",
      color: theme.palette.secondary.main,
    },
    {
      icon: <Security />,
      title: "Role Inheritance",
      description:
        "Developer Master → Admin → Faculty → Scholar → Student; higher tiers retain lower capabilities.",
      color: theme.palette.error.light,
    },
    {
      icon: <Engineering />,
      title: "Department Isolation",
      description:
        "Records auto-bound to admin's department; prevents cross-department leakage & edits.",
      color: theme.palette.info.main,
    },
  ];

  // Maintenance feature grouping
  const maintenanceFeatures = [
    {
      icon: <Handyman />,
      title: "Structured Requests",
      description:
        "Category / subcategory / priority captured with auto assignment metadata.",
      color: theme.palette.success.main,
    },
    {
      icon: <AssignmentTurnedIn />,
      title: "Technician Dispatch",
      description:
        "Admin & Manager assign; workload balancing & status transitions tracked.",
      color: theme.palette.warning.main,
    },
    {
      icon: <Build />,
      title: "Asset Integrity",
      description:
        "Calibration & condition context embedded in maintenance operations.",
      color: theme.palette.error.main,
    },
    {
      icon: <NotificationsActive />,
      title: "Real-Time Alerts",
      description:
        "Low stock, overdue tasks, escalations & closure events surfaced instantly.",
      color: theme.palette.warning.light,
    },
    {
      icon: <Analytics />,
      title: "Ops Analytics",
      description:
        "Turnaround time, assignment latency, closure rate & aging backlogs KPIs.",
      color: theme.palette.success.light,
    },
    {
      icon: <Psychology />,
      title: "Predictive Signals",
      description:
        "Failure & usage anomaly hooks prepared for ML enrichment modules.",
      color: theme.palette.info.main,
    },
  ];

  // Workflow visualization (maps to maintenance status lifecycle in codebase)
  const workflowSteps = [
    {
      label: "Submit",
      detail: "User files request with category & priority.",
      icon: <AutoAwesome />,
    },
    {
      label: "Review",
      detail: "Admin validates scope & enrichment.",
      icon: <Analytics />,
    },
    {
      label: "Assign",
      detail: "Manager/Admin allocates technician.",
      icon: <AssignmentTurnedIn />,
    },
    {
      label: "Execute",
      detail: "Worker updates progress & logs.",
      icon: <Engineering />,
    },
    {
      label: "Resolve",
      detail: "Outcome verified; status updated.",
      icon: <Handyman />,
    },
    {
      label: "Close",
      detail: "Final compliance & archival.",
      icon: <Security />,
    },
  ];

  // Removed mock stats and testimonials data. Sections depending on them are hidden until real data is integrated.
  const stats = [];
  const testimonials = [];

  return (
    <Box>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={isVisible} timeout={1000}>
                <Box>
                  <Chip
                    icon={<Rocket />}
                    label="🚀 Unified Lab & Maintenance Platform"
                    sx={{
                      mb: 3,
                      bgcolor: theme.palette.primary.main,
                      color: "white",
                      fontWeight: "bold",
                      px: 2,
                      py: 1,
                      "& .MuiChip-icon": { color: "white" },
                    }}
                  />

                  <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: "2.5rem", md: "4rem" },
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      mb: 2,
                      lineHeight: 1.1,
                    }}
                  >
                    SeeD-Organizr
                  </Typography>

                  <Typography
                    variant="h4"
                    color="text.primary"
                    paragraph
                    sx={{
                      mb: 4,
                      lineHeight: 1.4,
                      fontWeight: 500,
                      opacity: 0.9,
                    }}
                  >
                    The Future of{" "}
                    <Box
                      component="span"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: "bold",
                      }}
                    >
                      Laboratory & Maintenance Operations
                    </Box>
                  </Typography>

                  <Typography
                    variant="h6"
                    color="text.secondary"
                    paragraph
                    sx={{ mb: 5, lineHeight: 1.6 }}
                  >
                    Orchestrate inventory, bookings, maintenance requests,
                    technician assignment & compliance within a department-
                    scoped, role-inherited security model.
                  </Typography>

                  <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
                    <GradientButton
                      size="large"
                      endIcon={<ArrowForward />}
                      onClick={() => navigate("/login")}
                      sx={{ px: 4, py: 2 }}
                    >
                      Proceed to Login
                    </GradientButton>

                    <Button
                      size="large"
                      variant="outlined"
                      sx={{
                        borderRadius: "50px",
                        px: 4,
                        py: 2,
                        textTransform: "none",
                        fontWeight: "bold",
                        borderWidth: 2,
                        "&:hover": {
                          borderWidth: 2,
                          transform: "translateY(-2px)",
                        },
                      }}
                      onClick={() => navigate("/login")}
                    >
                      Access Portal
                    </Button>
                  </Stack>

                  {/* Removed marketing freebies line per requirements */}
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Slide direction="left" in={isVisible} timeout={1200}>
                <Box
                  sx={{ position: "relative", height: { xs: 300, md: 500 } }}
                >
                  <FloatingIcon
                    delay={0}
                    sx={{ position: "absolute", top: "10%", left: "10%" }}
                  >
                    <FeatureIcon color={theme.palette.primary.main}>
                      <Science />
                    </FeatureIcon>
                  </FloatingIcon>

                  <FloatingIcon
                    delay={1}
                    sx={{ position: "absolute", top: "20%", right: "15%" }}
                  >
                    <FeatureIcon color={theme.palette.secondary.main}>
                      <Engineering />
                    </FeatureIcon>
                  </FloatingIcon>

                  <FloatingIcon
                    delay={2}
                    sx={{ position: "absolute", bottom: "25%", left: "20%" }}
                  >
                    <FeatureIcon color={theme.palette.success.main}>
                      <Biotech />
                    </FeatureIcon>
                  </FloatingIcon>

                  <FloatingIcon
                    delay={0.5}
                    sx={{ position: "absolute", bottom: "10%", right: "10%" }}
                  >
                    <FeatureIcon color={theme.palette.warning.main}>
                      <AutoAwesome />
                    </FeatureIcon>
                  </FloatingIcon>

                  {/* Central Large Icon */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      animation: `${pulse} 4s ease-in-out infinite`,
                    }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: 120, md: 160 },
                        height: { xs: 120, md: 160 },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        border: `4px solid ${theme.palette.background.paper}`,
                        boxShadow: `0 16px 32px ${theme.palette.primary.main}30`,
                      }}
                    >
                      <Dashboard
                        sx={{ fontSize: { xs: "3rem", md: "4rem" } }}
                      />
                    </Avatar>
                  </Box>
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Stats Section (hidden - awaiting real data) */}
      {stats.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Fade in={isVisible} timeout={1000 + index * 200}>
                  <StatCard elevation={0}>
                    <Avatar
                      sx={{
                        bgcolor: `${stat.color}15`,
                        color: stat.color,
                        width: 60,
                        height: 60,
                        margin: "0 auto 16px",
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color={stat.color}
                    >
                      {stat.number}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      fontWeight="medium"
                    >
                      {stat.label}
                    </Typography>
                  </StatCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* Core Laboratory Operations */}
      <Box sx={{ bgcolor: "background.default", py: 12 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Chip
              icon={<TrendingUp />}
              label="Core Laboratory Operations"
              sx={{
                mb: 3,
                bgcolor: theme.palette.secondary.main,
                color: "white",
                fontWeight: "bold",
              }}
            />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Inventory • Booking • Data Models • Roles
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: 760, mx: "auto" }}
            >
              Inventory governance, slot scheduling, standardized categories &
              inherited role hierarchy.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {labFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <Fade in={isVisible} timeout={1200 + index * 150}>
                  <GlassmorphismCard sx={{ height: "100%" }}>
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <FeatureIcon color={feature.color}>
                        {feature.icon}
                      </FeatureIcon>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </GlassmorphismCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Maintenance & Reliability */}
      <Box sx={{ bgcolor: "background.paper", py: 12 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Chip
              icon={<TrendingUp />}
              label="Maintenance & Reliability"
              sx={{
                mb: 3,
                bgcolor: theme.palette.primary.main,
                color: "white",
                fontWeight: "bold",
              }}
            />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Request → Assignment → Execution → Closure
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ maxWidth: 860, mx: "auto" }}
            >
              Lifecycle enforced through role-scoped panels for Admin, Manager &
              Worker tiers.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {maintenanceFeatures.map((feature, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in={isVisible} timeout={1200 + index * 150}>
                  <GlassmorphismCard sx={{ height: "100%" }}>
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <FeatureIcon color={feature.color}>
                        {feature.icon}
                      </FeatureIcon>
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.5 }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </GlassmorphismCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Maintenance Workflow Visualization */}
      <Box
        sx={{
          py: 10,
          background: `linear-gradient(135deg, ${theme.palette.background.default}, ${theme.palette.background.paper})`,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            fontWeight="bold"
            align="center"
            gutterBottom
          >
            Maintenance Workflow
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 6, maxWidth: 760, mx: "auto" }}
          >
            Derived from platform code (maintenanceAPI & panels). Each
            transition is permission-gated & auditable.
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            {workflowSteps.map((step, idx) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={idx}>
                <StatCard sx={{ p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: `${theme.palette.primary.main}15`,
                      color: theme.palette.primary.main,
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    {step.icon}
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {step.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.4 }}
                  >
                    {step.detail}
                  </Typography>
                </StatCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonials Section (hidden - awaiting real data) */}
      {testimonials.length > 0 && (
        <Container maxWidth="lg" sx={{ py: 12 }}>
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              Trusted by Leading Universities
            </Typography>
            <Typography variant="h5" color="text.secondary">
              See what researchers are saying
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={isVisible} timeout={1400 + index * 200}>
                  <GlassmorphismCard>
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          margin: "0 auto 24px",
                          bgcolor: theme.palette.primary.main,
                          fontSize: "1.5rem",
                          fontWeight: "bold",
                        }}
                      >
                        {testimonial.avatar}
                      </Avatar>
                      <Typography
                        variant="h6"
                        paragraph
                        sx={{ fontStyle: "italic", mb: 3 }}
                      >
                        "{testimonial.quote}"
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {testimonial.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testimonial.role}
                      </Typography>
                    </CardContent>
                  </GlassmorphismCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      )}

      {/* CTA Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}95, ${theme.palette.secondary.main}95)`,
          color: "white",
          py: 12,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M20 20c0 11.046-8.954 20-20 20s-20-8.954-20-20 8.954-20 20-20 20 8.954 20 20z'/%3E%3C/g%3E%3C/svg%3E")`,
          },
        }}
      >
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Typography variant="h2" fontWeight="bold" gutterBottom>
            Operational Convergence
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.9 }}>
            Inventory, booking, maintenance lifecycle & workforce
            assignment—aligned under departmental security.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            justifyContent="center"
          >
            <GradientButton
              size="large"
              variant="success"
              endIcon={<ArrowForward />}
              onClick={() => navigate("/login")}
              sx={{
                px: 5,
                py: 2.5,
                fontSize: "1.2rem",
                background: "rgba(255,255,255,0.2)",
                backdropFilter: "blur(10px)",
                border: "2px solid rgba(255,255,255,0.3)",
                "&:hover": {
                  background: "rgba(255,255,255,0.3)",
                },
              }}
            >
              Login
            </GradientButton>

            <Button
              size="large"
              variant="outlined"
              sx={{
                px: 5,
                py: 2.5,
                fontSize: "1.2rem",
                borderColor: "rgba(255,255,255,0.5)",
                color: "white",
                borderWidth: 2,
                borderRadius: "50px",
                "&:hover": {
                  borderColor: "white",
                  borderWidth: 2,
                  background: "rgba(255,255,255,0.1)",
                },
              }}
              onClick={() => navigate("/login")}
            >
              Role Overview
            </Button>
          </Stack>

          {/* Removed academic free / enterprise tagline per requirements */}
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: "background.paper",
          py: 6,
          textAlign: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
            SeeD-Organizr
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Unified platform for laboratory operations, maintenance workflows,
            and departmental asset governance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            © 2025 SeeD-Organizr. All rights reserved. | Made with ❤️ for
            researchers
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
